import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Avatar, Stack } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classNames from "classnames";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { SubscriptionContext } from "../../../contexts/SubscriptionContext";
import { getUserEmail } from "../../../helpers/authUser";
import { scrollbarStyle } from '../../../shared/dashboard';
import EnterprisePlan from '../../molecules/subscription-plans/EnterprisePlan';
import GrowthPlan from '../../molecules/subscription-plans/GrowthPlan';
import ProPlan from "../../molecules/subscription-plans/ProPlan";

export enum SubscriptionPlan {
    Free = 'free',
    Pro_v1 = 'pro_v1_monthly',
    Pro_v2 = 'pro_v2_monthly',
    Pro_Legacy = 'charlie',
    Growth_v1 = 'growth_v1_monthly',
    Growth_v2 = 'growth_v2_monthly',
    PowerUser = 'power_v1_monthly',
    Enterprise = 'enterprise_v1_monthly',
};

export enum SubscriptionSeatLimit {
    /* cont-tf-mig */
    price_1S6ck4C4CyYG72UKj0LqlJKX = 1,
    price_1S6cqlC4CyYG72UKE8oCR2U0 = 3,
    // price_1S8WV3C4CyYG72UKMZmPe6d6 = 6,
    /* app */
    price_1S6dcdC4CyYG72UKvVlU2ac4 = 1,
    price_1S6dcZC4CyYG72UK5UaG7eNM = 3,
    price_1S8WV3C4CyYG72UKMZmPe6d6 = 6,
};

export enum SubscriptionMonthlyLimit {
    Free = 0,
    Pro_v1 = 100,
    Pro_v2 = 100,
    Pro_Legacy = 50,
    Growth_v1 = 300,
    Growth_v2 = 300,
    PowerUser = +Infinity,
    Enterprise = +Infinity,
};

const useStyles = makeStyles((theme) => ({
    planCard: {
        position: 'relative',
        display: 'inline-block',
        minWidth: 400,
        width: '100%',
        maxWidth: 480,
        height: 528,
        marginTop: 8,
        borderRadius: 32,
        padding: '16px 24px',
        border: `2px solid ${theme.colors.neutral['200']}`,
        boxShadow: '0px 4px 10px -1px rgba(16, 24, 40, 0.06)',
        transition: '0.05s ease-in-out',
        "&:hover": {
            boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40, 0.14), 0px 1px 14px 0px rgba(16, 24, 40, 0.12)',
        },
    },
    selectedCard: {
        border: `2px solid ${theme.colors.cyan['400']}`,
        "&:hover": {
            border: `2px solid ${theme.colors.cyan['400']}`,
        }
    },
    selectionMark: {
        position: 'relative',
        width: 60,
        height: 60,
        bottom: `calc(100% + calc(${2 * 60}px / 3))`,
        left: `calc(100% - calc(${60}px / 3))`,
        background: theme.colors.cyan[50],
        border: `2px solid ${theme.colors.cyan['400']}`,
    },
    selectionMarkIcon :{
        padding: 0,
        width: 50,
        height: 50,
        fill: theme.colors.primary[400],
    },
}));

const SubscriptionSelector: React.FC<{
    onShowManage: (state: boolean) => void,
    onSelectSubscription: () => void,
}> = ({ onShowManage, onSelectSubscription }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const { subscription } = useContext(SubscriptionContext);

    const [loaded, setLoaded] = useState<boolean>(false);
    const [, setFreePlanSelect] = useState<boolean>(false);
    const [proPlanSelect, setProPlanSelect] = useState<boolean>(false);
    const [growthPlanSelect, setGrowthPlanSelect] = useState<boolean>(false);
    const [powerPlanSelect, setPowerPlanSelect] = useState<boolean>(false);
    const [enterprisePlanSelect, setEnterprisePlanSelect] = useState<boolean>(false)
    const [cancelled, setCancelled] = useState<boolean>(false);
    const [paymentReceived, setPaymentReceived] = useState<boolean>(false);
    const [trialDays, setTrialDays] = useState<number | undefined>(undefined);
    const [admin, setAdmin] = useState<string>('');

    const handleSelectPlan = useCallback((plan: SubscriptionPlan|string) => {
        switch (plan) {
            case SubscriptionPlan.Free:
                setFreePlanSelect(true);
                setProPlanSelect(false);
                setGrowthPlanSelect(false);
                setPowerPlanSelect(false);
                setEnterprisePlanSelect(false);
                break;
            default:
                setFreePlanSelect(false);
                setProPlanSelect((plan === SubscriptionPlan.Pro_v1) || (plan === SubscriptionPlan.Pro_v2) || (plan === SubscriptionPlan.Pro_Legacy));
                setGrowthPlanSelect((plan === SubscriptionPlan.Growth_v1) || (plan === SubscriptionPlan.Growth_v2));
                setPowerPlanSelect(plan === SubscriptionPlan.PowerUser);
                setEnterprisePlanSelect(plan === SubscriptionPlan.Enterprise);
                setEnterprisePlanSelect(plan.includes('enterprise_'));
            break;
        }
    }, []);

    const handleSubscription = useCallback((subscription: any) => {
        if (subscription?.cancelAt) {
            const fromCancelDiff = moment(subscription?.cancelAt).diff(moment(), 'days');
            if (fromCancelDiff >= 0)
                setTrialDays(fromCancelDiff);
            handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
            setCancelled(true);
        } else if (subscription.status === 'trialing') {
            let trialDaysCount = 0;
            if (subscription?.trialEndedAt)
                trialDaysCount = moment(subscription?.trialEndedAt).diff(moment(), 'days');
            if (trialDaysCount >= 0 && subscription?.paymentMethodId) {
                setTrialDays(trialDaysCount);
                handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
                setPaymentReceived(true);
            } else if (trialDaysCount >= 0) {
                setTrialDays(trialDaysCount);
                handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
            } else if (subscription?.paymentMethodId) {
                handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
                setPaymentReceived(true);
            } else {
                setFreePlanSelect(true);
            }
        } else if (subscription.status === 'active') {
            handleSelectPlan(subscription.planName.toLowerCase() as SubscriptionPlan);
        }
    }, [handleSelectPlan]);

    useEffect(() => {
        onShowManage(loaded
            && (proPlanSelect || growthPlanSelect || powerPlanSelect || enterprisePlanSelect)
            && (admin === getUserEmail(user).toLowerCase()));
    }, [loaded, proPlanSelect, growthPlanSelect, powerPlanSelect, enterprisePlanSelect, admin, user, onShowManage]);

    useEffect(() => {
        if (subscription) {
            switch (subscription.planName?.toLowerCase()) {
                case SubscriptionPlan.Enterprise:
                case SubscriptionPlan.PowerUser:
                case SubscriptionPlan.Growth_v1:
                case SubscriptionPlan.Growth_v2:
                case SubscriptionPlan.Pro_v1:
                case SubscriptionPlan.Pro_v2:
                case SubscriptionPlan.Pro_Legacy:
                    handleSubscription(subscription);
                    break;
                case SubscriptionPlan.Free:
                    handleSelectPlan(SubscriptionPlan.Free);
                    break;
                default:
                    if (subscription.planName?.includes('enterprise_'))
                        handleSubscription(subscription);
                    break;
            }
            setAdmin(subscription?.user!);
            setLoaded(true);
        }
    }, [handleSelectPlan, handleSubscription, subscription, user]);

    return (<>
        <Stack direction="column" alignItems="center" justifyContent="center" pt={3} width="100%">
            <Stack direction="row" spacing={6} alignItems="center" justifyContent="flex-start" width="auto" p={2} px={4}
                sx={{ overflowX: 'auto', ...scrollbarStyle }}>
                {/* <Stack direction="column" className={classNames(classes.planCard, freePlanSelect && classes.selectedCard)}>
                    <FreePlan selected={freePlanSelect}
                        admin={admin}
                        daysLeft={trialDays}
                        hasPayment={paymentReceived}
                        loaded={loaded} />
                    {freePlanSelect && (
                        <Avatar className={classes.selectionMark}>
                            <CheckRoundedIcon className={classes.selectionMarkIcon} />
                        </Avatar>
                    )}
                </Stack> */}
                <Stack direction="column" className={classNames(classes.planCard, proPlanSelect && classes.selectedCard)}>
                    <ProPlan admin={admin}
                        daysLeft={proPlanSelect ? trialDays : undefined}
                        cancelled={cancelled}
                        hasPayment={paymentReceived}
                        loaded={loaded} />
                    {proPlanSelect && (
                        <Avatar className={classes.selectionMark}>
                            <CheckRoundedIcon className={classes.selectionMarkIcon} />
                        </Avatar>
                    )}
                </Stack>
                <Stack direction="column" className={classNames(classes.planCard, growthPlanSelect && classes.selectedCard)}>
                    <GrowthPlan admin={admin}
                        daysLeft={growthPlanSelect? trialDays : undefined}
                        cancelled={cancelled}
                        hasPayment={paymentReceived}
                        loaded={loaded} />
                    {growthPlanSelect && (
                        <Avatar className={classes.selectionMark}>
                            <CheckRoundedIcon className={classes.selectionMarkIcon} />
                        </Avatar>
                    )}
                </Stack>
                {/* <Stack direction="column" className={classNames(classes.planCard, powerPlanSelect && classes.selectedCard)}>
                    <PowerUserPlan selected={powerPlanSelect}
                        admin={admin}
                        daysLeft={powerPlanSelect? trialDays : undefined}
                        cancelled={cancelled}
                        hasPayment={paymentReceived}
                        loaded={loaded}
                        upgrade={!powerPlanSelect}
                        onSelectPlan={onSelectSubscription} />
                    {powerPlanSelect && (
                        <Avatar className={classes.selectionMark}>
                            <CheckRoundedIcon className={classes.selectionMarkIcon} />
                        </Avatar>
                    )}
                </Stack> */}
                <Stack direction="column" className={classNames(classes.planCard, enterprisePlanSelect && classes.selectedCard)}>
                    <EnterprisePlan selected={enterprisePlanSelect}
                        admin={admin}
                        price={(subscription?.pricePerSeatCents || 0) / 100}
                        seats={subscription?.planId! in SubscriptionSeatLimit ? SubscriptionSeatLimit[subscription?.planId as keyof typeof SubscriptionSeatLimit] : 1}
                        loaded={loaded}
                        upgrade={!enterprisePlanSelect}
                        onSelectPlan={onSelectSubscription} />
                    {enterprisePlanSelect && (
                        <Avatar className={classes.selectionMark}>
                            <CheckRoundedIcon className={classes.selectionMarkIcon} />
                        </Avatar>
                    )}
                </Stack>
            </Stack>
        </Stack>
    </>);
};

export default SubscriptionSelector;